"use client";

import React from 'react';
import { Link } from 'react-router-dom';
import { Table } from '../../ui/components/Table';
import { Button } from '../../ui/components/Button';
import type {
  RichlistResult,
  SimpleHolderEntry,
  PointsHolderEntry,
} from '../../api/richlist';

interface RichlistTableProps {
  richlist: RichlistResult;
  collectionName?: string;
}

/**
 * Check if this is a simple single-tier points strategy
 * (e.g., 0.5, 1, 2, 5, 10, or 100 points per NFT)
 * In these cases, we hide the tier breakdown columns since all NFTs have the same value
 */
function isSingleTierStrategy(pointTiers: number[]): boolean {
  return pointTiers.length === 1;
}

/**
 * Check if the Points column should be hidden
 * Only hide when it's exactly 1 point per NFT (points = NFT count)
 * For other single-tier strategies (0.5, 2, 5, etc.), show Points since it differs from NFT count
 */
function shouldHidePointsColumn(pointTiers: number[]): boolean {
  return pointTiers.length === 1 && pointTiers[0] === 1;
}

/**
 * Wise Guys strategy tier labels
 * Maps point values to custom column names
 */
const WISE_GUYS_TIER_LABELS: Record<number, string> = {
  1: 'Crooked Cop',
  2: 'Enforcer',
  3: 'Mob Lieutenant',
  4: 'Head Of Family',
  10: 'The Don',
};

/**
 * Placers strategy tier labels
 * Maps point values to custom column names
 */
const PLACERS_TIER_LABELS: Record<number, string> = {
  1: 'Commons',
  3: 'Rare',
  5: 'Legendaries',
  8: '1/1s',
};

/**
 * Format a point value for display
 * Removes unnecessary trailing zeros (e.g., 1.00 -> 1, 0.50 -> 0.5)
 * Note: Value may come as string from API (PostgreSQL DECIMAL)
 */
function formatPointValue(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  // If it's a whole number, display without decimals
  if (Number.isInteger(num)) {
    return num.toLocaleString();
  }
  // Otherwise, format with up to 2 decimal places, removing trailing zeros
  // Use toLocaleString for thousands separators, then clean up trailing zeros
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Get tier column label based on strategy
 * Returns JSX for Wise Guys and Placers (name + points on separate lines), string for others
 */
function getTierLabel(tier: number, strategy?: string | null): React.ReactNode {
  if (strategy === 'wise_guys' && WISE_GUYS_TIER_LABELS[tier]) {
    return (
      <div className="flex flex-col">
        <span>{WISE_GUYS_TIER_LABELS[tier]}</span>
        <span className="text-xs text-subtext-color">({formatPointValue(tier)} pts)</span>
      </div>
    );
  }
  if (strategy === 'placers' && PLACERS_TIER_LABELS[tier]) {
    return (
      <div className="flex flex-col">
        <span>{PLACERS_TIER_LABELS[tier]}</span>
        <span className="text-xs text-subtext-color">({formatPointValue(tier)} DAP)</span>
      </div>
    );
  }
  return `${formatPointValue(tier)}-Point NFTs`;
}

/**
 * Get wallet page URL
 */
function getWalletUrl(address: string): string {
  return `/wallets/${address}`;
}

/**
 * Export richlist data to CSV and trigger download
 */
function exportRichlistToCsv(richlist: RichlistResult, collectionName?: string): void {
  const rows: string[] = [];

  if (richlist.has_points_system) {
    // Build header row with tier columns
    const pointTiers = richlist.point_tiers;
    const isSimplePoints = isSingleTierStrategy(pointTiers);
    const hidePointsColumn = shouldHidePointsColumn(pointTiers);

    const headers = ['Rank', 'Holder'];
    if (!isSimplePoints) {
      for (const tier of pointTiers) {
        headers.push(`${formatPointValue(tier)}-Point NFTs`);
      }
    }
    headers.push('NFTs');
    if (!hidePointsColumn) {
      headers.push('Points');
    }
    rows.push(headers.join(','));

    // Add data rows
    for (const holder of richlist.holders) {
      const row: (string | number)[] = [holder.rank, holder.owner_address];
      if (!isSimplePoints) {
        for (const tier of pointTiers) {
          row.push(holder.tiers[tier.toString()] || 0);
        }
      }
      row.push(holder.nft_count);
      if (!hidePointsColumn) {
        row.push(holder.total_points);
      }
      rows.push(row.join(','));
    }
  } else {
    // Simple richlist without points
    rows.push('Rank,Holder,NFTs');
    for (const holder of richlist.holders) {
      rows.push(`${holder.rank},${holder.owner_address},${holder.nft_count}`);
    }
  }

  // Create and trigger download
  const csv = rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = collectionName
    ? `richlist-${collectionName.toLowerCase().replace(/\s+/g, '-')}.csv`
    : 'richlist.csv';
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Simple richlist table (no points system)
 */
function SimpleRichlistTable({ holders, onExport }: { holders: SimpleHolderEntry[]; onExport: () => void }) {
  if (holders.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-subtext-color">
        No holders found
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Summary stats with export button */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm text-subtext-color">
          <span>Holders: <strong className="text-default-font">{holders.length.toLocaleString()}</strong></span>
        </div>
        <Button
          variant="neutral-secondary"
          size="small"
          onClick={onExport}
        >
          Export CSV
        </Button>
      </div>

      <Table
      header={
        <Table.HeaderRow>
          <Table.HeaderCell className="w-16">Rank</Table.HeaderCell>
          <Table.HeaderCell>Holder</Table.HeaderCell>
          <Table.HeaderCell className="text-right">NFTs</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {holders.map((holder) => (
        <Table.Row key={holder.owner_address}>
          <Table.Cell className="w-16">
            <span className="text-body-bold font-body-bold text-default-font">
              #{holder.rank}
            </span>
          </Table.Cell>
          <Table.Cell>
            <Link
              to={getWalletUrl(holder.owner_address)}
              className="text-body font-body text-brand-700 hover:text-brand-600 underline font-mono text-sm"
            >
              {holder.owner_address}
            </Link>
          </Table.Cell>
          <Table.Cell className="text-right">
            <span className="text-body-bold font-body-bold text-default-font">
              {holder.nft_count.toLocaleString()}
            </span>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
    </div>
  );
}

/**
 * Points-based richlist table with dynamic tier columns
 */
function PointsRichlistTable({
  holders,
  pointTiers,
  totalPoints,
  strategy,
  onExport,
}: {
  holders: PointsHolderEntry[];
  pointTiers: number[];
  totalPoints: number;
  strategy?: string | null;
  onExport: () => void;
}) {
  if (holders.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-subtext-color">
        No holders found
      </div>
    );
  }

  // For single-tier strategies (like 0.5, 1, 2, 5, 10, 100 points per NFT), hide redundant tier columns
  const isSimplePoints = isSingleTierStrategy(pointTiers);
  // Only hide Points column when it's 1 point per NFT (truly redundant with NFT count)
  const hidePointsColumn = shouldHidePointsColumn(pointTiers);

  return (
    <div className="flex flex-col gap-4">
      {/* Summary stats with export button */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-sm text-subtext-color">
          <span>Total Points: <strong className="text-default-font">{formatPointValue(totalPoints)}</strong></span>
          <span>Holders: <strong className="text-default-font">{holders.length.toLocaleString()}</strong></span>
        </div>
        <Button
          variant="neutral-secondary"
          size="small"
          onClick={onExport}
        >
          Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell className="w-16">Rank</Table.HeaderCell>
              <Table.HeaderCell>Holder</Table.HeaderCell>
              {/* Dynamic tier columns - hide when single-tier strategy */}
              {!isSimplePoints && pointTiers.map((tier) => (
                <Table.HeaderCell key={tier} className="text-center min-w-[100px]">
                  {getTierLabel(tier, strategy)}
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell className="justify-center">NFTs</Table.HeaderCell>
              {/* Points column - only hide when 1 point per NFT (truly redundant with NFT count) */}
              {!hidePointsColumn && (
                <Table.HeaderCell className="justify-center">Points</Table.HeaderCell>
              )}
            </Table.HeaderRow>
          }
        >
          {holders.map((holder) => (
            <Table.Row key={holder.owner_address}>
              <Table.Cell className="w-16">
                <span className="text-body-bold font-body-bold text-default-font">
                  #{holder.rank}
                </span>
              </Table.Cell>
              <Table.Cell>
                <Link
                  to={getWalletUrl(holder.owner_address)}
                  className="text-body font-body text-brand-700 hover:text-brand-600 underline font-mono text-sm"
                >
                  {holder.owner_address}
                </Link>
              </Table.Cell>
              {/* Dynamic tier counts - hide when single-tier strategy */}
              {!isSimplePoints && pointTiers.map((tier) => {
                const count = holder.tiers[tier.toString()] || 0;
                return (
                  <Table.Cell key={tier} className="justify-center">
                    <span className={`text-body font-body ${count > 0 ? 'text-default-font' : 'text-subtext-color'}`}>
                      {count > 0 ? count : '-'}
                    </span>
                  </Table.Cell>
                );
              })}
              <Table.Cell className="justify-center">
                <span className="text-body-bold font-body-bold text-default-font">
                  {holder.nft_count.toLocaleString()}
                </span>
              </Table.Cell>
              {/* Points column - only hide when 1 point per NFT */}
              {!hidePointsColumn && (
                <Table.Cell className="justify-center">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {formatPointValue(holder.total_points)}
                  </span>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table>
      </div>
    </div>
  );
}

/**
 * RichlistTable component
 * Renders either simple holder list or points-based richlist with dynamic columns
 */
export function RichlistTable({ richlist, collectionName }: RichlistTableProps) {
  const handleExportCsv = () => {
    exportRichlistToCsv(richlist, collectionName);
  };

  if (richlist.has_points_system) {
    return (
      <PointsRichlistTable
        holders={richlist.holders}
        pointTiers={richlist.point_tiers}
        totalPoints={richlist.total_points}
        strategy={richlist.strategy}
        onExport={handleExportCsv}
      />
    );
  }

  return <SimpleRichlistTable holders={richlist.holders} onExport={handleExportCsv} />;
}
